// Single source of truth for classifying a vehicle by type / customer segment.
//
//  - vehicleType():       granular GA4 `item_category` — 'car' | 'van' | 'bus'.
//                         Minibuses are tagged 'minibussi' and reported as 'bus'.
//  - vehicleSegmentOf():  binary customer segment — 'car' | 'van'.
//                         Minibus/bus roll up into 'car' (passenger transport);
//                         only a pakettiauto (`type === 'van'`) is 'van'.
//
// Previously this logic was duplicated in ga4Ecommerce.ts and PurchaseGtagEvent.ts.
// Kept dependency-free and defensive so it can never throw and break the booking flow.

import { VehicleClassInput } from '../interfaces/VehicleClassInput';

export type VehicleSegment = 'car' | 'van';
export type { VehicleClassInput };

/** Granular class used for GA4 `item_category`. */
export const vehicleType = (v?: VehicleClassInput | null): 'car' | 'van' | 'bus' =>
  (v?.tagCloud || []).includes('minibussi') ? 'bus'
    : v?.vehicleModelData?.type === 'van' ? 'van'
      : 'car';

/** Binary customer segment (minibus/bus → car). */
export const vehicleSegmentOf = (v?: VehicleClassInput | null): VehicleSegment =>
  v?.vehicleModelData?.type === 'van' ? 'van' : 'car';

/**
 * The segment the customer actually EXPRESSED, or undefined if they expressed none.
 * Precedence:
 *   1. the explicit choice (split search buttons / results toggle / `?segment=`)
 *   2. the active body-type filter ('van' ⇒ van, any other body type ⇒ car)
 * Unlike segmentFromSearchParams() this does NOT fall back to 'car', so callers can
 * tell "chose car" apart from "said nothing". Use it for anything that records intent.
 */
export function expressedSegmentFromSearchParams(
  sp?: { vehicleSegment?: string; vehicleType?: string } | null
): VehicleSegment | undefined {
  if (!sp) return undefined;
  if (sp.vehicleSegment === 'van' || sp.vehicleSegment === 'car') return sp.vehicleSegment;
  if (sp.vehicleType) return sp.vehicleType === 'van' ? 'van' : 'car';
  return undefined;
}

/**
 * Segment for a whole search, for GA4 funnel analysis. Precedence:
 *   1. the explicit choice (search buttons / results toggle)
 *   2. the active body-type filter ('van' ⇒ van)
 *   3. default 'car'
 * Returns undefined only when there is no search context at all, so callers can
 * omit the param entirely before the user has interacted with search.
 *
 * NOTE: because of step 3, 'car' here means "chose car OR expressed nothing".
 * For intent, use expressedSegmentFromSearchParams() instead.
 */
export function segmentFromSearchParams(
  sp?: { vehicleSegment?: string; vehicleType?: string } | null
): VehicleSegment | undefined {
  if (!sp) return undefined;
  return expressedSegmentFromSearchParams(sp) ?? 'car';
}
